import { formatDateTime } from "@/shared/lib/date-time";
import { PresignedFileUpload } from "@/shared/ui/PresignedFileUpload";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@devhop/ui";
import { DownloadIcon, EyeIcon, FileTextIcon, Trash2Icon } from "lucide-react";
import { useMemo } from "react";
import { studentsApi } from "../api/client";
import { useSetStudentMembershipSignedDocument } from "../api/queries";

function isPdfKey(key: string): boolean {
  return key.toLowerCase().endsWith(".pdf");
}

function isImageKey(key: string): boolean {
  return /\.(jpe?g|png|gif|webp|bmp)$/i.test(key);
}

type StudentMembershipSignedDocumentSectionProps = {
  studentId: string;
  fileId: string | null;
  uploadedAt: string | null;
  canEdit: boolean;
};

export function StudentMembershipSignedDocumentSection({
  studentId,
  fileId,
  uploadedAt,
  canEdit,
}: StudentMembershipSignedDocumentSectionProps) {
  const setDocument = useSetStudentMembershipSignedDocument(studentId);
  const fileUrl = useMemo(
    () =>
      fileId ? studentsApi.membershipSignedDocumentUrl(studentId) : null,
    [studentId, fileId],
  );

  const handleUploadSuccess = (key: string) => {
    setDocument.mutate({ fileId: key });
  };

  const handleRemove = () => {
    if (!fileId) return;
    if (!window.confirm("ລຶບເອກະສານສະມາຊິກທີ່ລົງນາມແລ້ວ?")) return;
    setDocument.mutate({ fileId: null });
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          <FileTextIcon className="size-4" />
          ເອກະສານສະມາຊິກທີ່ລົງນາມແລ້ວ
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {fileId && fileUrl ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-muted-foreground text-sm">
                {uploadedAt
                  ? `ອັບໂຫຼດເມື່ອ ${formatDateTime(uploadedAt)}`
                  : "ມີເອກະສານແລ້ວ"}
              </p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(fileUrl, "_blank")}
                >
                  <EyeIcon className="size-4" />
                  ເບິ່ງ
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(fileUrl, "_blank")}
                >
                  <DownloadIcon className="size-4" />
                  ດາວໂຫຼດ
                </Button>
                {canEdit && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={setDocument.isPending}
                    onClick={() => void handleRemove()}
                  >
                    <Trash2Icon className="size-4" />
                    ລຶບ
                  </Button>
                )}
              </div>
            </div>

            <div className="overflow-hidden rounded-lg border bg-muted/20">
              {isPdfKey(fileId) ? (
                <iframe
                  title="Membership signed document preview"
                  src={fileUrl}
                  className="h-[480px] w-full bg-white"
                />
              ) : isImageKey(fileId) ? (
                <img
                  src={fileUrl}
                  alt="Membership signed document"
                  className="mx-auto max-h-[480px] w-full bg-white object-contain"
                />
              ) : (
                <div className="flex h-40 items-center justify-center text-muted-foreground text-sm">
                  ບໍ່ສາມາດ preview ຮູບແບບນີ້ໄດ້ — ໃຊ້ປຸ່ມ ເບິ່ງ / ດາວໂຫຼດ
                </div>
              )}
            </div>
          </>
        ) : (
          <p className="text-muted-foreground text-sm">
            ຍັງບໍ່ມີເອກະສານສະມາຊິກທີ່ລົງນາມແລ້ວ
          </p>
        )}

        {canEdit && (
          <PresignedFileUpload
            keyPrefix={`uploads/students/${studentId}/membership-signed`}
            accept="image/*,.pdf"
            buttonLabel={
              fileId ? "ອັບໂຫຼດເອກະສານໃໝ່" : "ອັບໂຫຼດເອກະສານທີ່ລົງນາມແລ້ວ"
            }
            hint="ຮອງຮັບຮູບພາບ ຫຼື PDF (scan ໃບຢືນຢັນສະມາຊິກຫຼັງເຊັນ)"
            onSuccess={handleUploadSuccess}
          />
        )}
      </CardContent>
    </Card>
  );
}
